import React, { useEffect, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TabsComponent from '@components/tabs/tabs.tsx';
import { TabVariant } from '@utils/tabs.utils.ts';
import { DesktopIcon } from '@shared/components/icons/svg-icon.tsx';
import ScreenMediaPreview from '@modules/digital-signage/components/screen-media-preview/screen-media-preview.tsx';
import {
  GroupScreenPreviewProps,
  ScreenOrientationEnum
} from '@modules/digital-signage/model/group-settings.model.ts';
import { SCREEN_ORIENTATION_TABS } from '../../group-settings.constants.tsx';

// p-4 on the preview body + 1px card border on each side
const PREVIEW_SPACE = 34;
const PORTRAIT_RATIO = 9 / 16;
const LANDSCAPE_RATIO = 16 / 9;
// Portrait frame at a pure 9:16 ratio looks too narrow in the popup
const PORTRAIT_EXTRA_WIDTH = 150;

const GroupScreenPreview: React.FC<GroupScreenPreviewProps> = ({ screen }) => {
  const { t } = useTranslation();
  const [orientation, setOrientation] = useState<ScreenOrientationEnum>(
    ScreenOrientationEnum.Landscape
  );

  const wrapperRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [available, setAvailable] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const isPortrait = orientation === ScreenOrientationEnum.Portrait;
  const hasMedia = !!(screen?.media || screen?.mediaUrl);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const header = headerRef.current;
    if (!wrapper || !header) return;

    const observer = new ResizeObserver(() => {
      const maxHeight =
        wrapper.clientHeight - header.offsetHeight - PREVIEW_SPACE;
      const maxWidth = wrapper.clientWidth - PREVIEW_SPACE;
      if (maxHeight <= 0 || maxWidth <= 0) return;

      setAvailable({ width: maxWidth, height: maxHeight });
    });

    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  // The card size always follows the landscape frame, so switching the
  // orientation only resizes the frame inside it, not the card itself.
  const landscapeHeight = available
    ? Math.min(available.height, available.width / LANDSCAPE_RATIO)
    : 0;
  const landscapeWidth = landscapeHeight * LANDSCAPE_RATIO;
  const frameSize = available
    ? isPortrait
      ? {
          width: Math.min(
            landscapeHeight * PORTRAIT_RATIO + PORTRAIT_EXTRA_WIDTH,
            landscapeWidth
          ),
          height: landscapeHeight
        }
      : { width: landscapeWidth, height: landscapeHeight }
    : undefined;

  return (
    <div
      ref={wrapperRef}
      className="flex flex-1 min-w-0 min-h-0 items-start justify-start"
    >
      <div
        className="flex flex-col max-w-full border border-(--gray-9) rounded-[14px] bg-(--white)"
        style={
          available ? { width: landscapeWidth + PREVIEW_SPACE } : undefined
        }
      >
        <div
          ref={headerRef}
          className="flex justify-between items-center gap-4 p-4 border-b border-(--gray-9)"
        >
          <div className="flex flex-col gap-1">
            <Typography
              variant="subtitle4"
              className="text-(--dark-1)! max-lg-xl:text-[16px]!"
            >
              {t('digital_signage_screen_preview')}
            </Typography>
            <Typography
              variant="caption"
              className="text-(--blue-4)! font-normal!"
            >
              {t('digital_signage_screen_preview_hint')}
            </Typography>
          </div>

          <div className="w-fit shrink-0">
            <TabsComponent
              value={orientation}
              variant={TabVariant.Segmented}
              tabs={SCREEN_ORIENTATION_TABS}
              onChange={(_, value) =>
                setOrientation(value as ScreenOrientationEnum)
              }
            />
          </div>
        </div>

        <div className="flex items-center justify-center p-4 overflow-hidden">
          <div
            className="overflow-hidden rounded-xl bg-(--primary-50) flex items-center justify-center transition-all duration-500"
            style={frameSize}
          >
            {hasMedia ? (
              <div className="w-full h-full">
                <ScreenMediaPreview screen={screen} />
              </div>
            ) : (
              <DesktopIcon
                stroke="var(--gray-7)"
                sx={{ width: 64, height: 64 }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupScreenPreview;
